import { BerthResponseDto, BerthSide, PierResponseDto } from '@planning/app/api'
import {
  carrierVisitScheduleDefaultValues,
  ICarrierVisitSchedule,
} from '@planning/components/CarrierVisitSchedule'
import { BerthItemStore } from '@planning/rt-stores/berth/BerthItemStore'
import {
  validateEtaDate,
  validateEtaDatetime,
  validateEtbDate,
  validateEtbDatetime,
  validateEtdDate,
  validateEtdDatetime,
} from '@planning/utils/visit-timing-utils'
import { FieldStore, FormStore, requiredArray, requiredDate, validateSubForm } from '@tom-ui/utils'
import { Dayjs } from 'dayjs'
import { action, computed, makeObservable } from 'mobx'
import { VesselVisitScheduleFormStore } from './VesselVisitScheduleFormStore'

export type VisitType = 'Single visit' | 'Repeating visit'

export type VesselVisitBerthAndTimingFormStoreType = {
  etaDate: Dayjs | null
  etaTime: Dayjs | null
  etbDate: Dayjs | null
  etbTime: Dayjs | null
  etdDate: Dayjs | null
  etdTime: Dayjs | null
  visitType: VisitType | null
  schedule: ICarrierVisitSchedule | null
  length: number | null
  draftArrival: number | null
  draftDeparture: number | null
  dockingSide: BerthSide
  pier: PierResponseDto | null
  qmmFrom: number | null
  qmmTo: number | null
  berths?: BerthResponseDto[] | null
}

export class VesselVisitBerthAndTimingFormStore extends FormStore<VesselVisitBerthAndTimingFormStoreType> {
  etaDate: FieldStore<Dayjs | null>
  etaTime: FieldStore<Dayjs | null>
  etbDate: FieldStore<Dayjs | null>
  etbTime: FieldStore<Dayjs | null>
  etdDate: FieldStore<Dayjs | null>
  etdTime: FieldStore<Dayjs | null>

  visitType: FieldStore<VisitType | null>
  schedule: VesselVisitScheduleFormStore

  length: FieldStore<number | ''>
  draftArrival: FieldStore<number | null>
  draftDeparture: FieldStore<number | null>

  dockingSide: FieldStore<BerthSide>
  pier: FieldStore<PierResponseDto | null>
  berths: FieldStore<BerthResponseDto[]>

  qmmFrom: FieldStore<number | ''>
  qmmTo: FieldStore<number | ''>

  constructor(
    private berthItemStore: BerthItemStore,
    initialValues?: Partial<VesselVisitBerthAndTimingFormStoreType>,
  ) {
    super(initialValues)

    makeObservable(this, {
      setData: action,
      reset: action,
      minQmmFrom: computed,
      maxQmmTo: computed,
      isQmmOutOfRange: computed,
      conflictingBerthDraft: computed,
      berthsOfSelectedPier: computed,
    })

    this.etaDate = new FieldStore<Dayjs | null>(
      initialValues?.etaDate ?? null,
      (val): string | undefined =>
        validateEtaDate({
          etaDate: val,
          etbDate: this.etbDate.value,
          etdDate: this.etdDate.value,
        }) || requiredDate()(val),
    )
    this.etaTime = new FieldStore<Dayjs | null>(
      initialValues?.etaTime ?? null,
      (val): string | undefined =>
        validateEtaDatetime({
          etaTime: val,
          etbTime: this.etbTime.value,
          etdTime: this.etdTime.value,
          etaDate: this.etaDate.value,
          etbDate: this.etbDate.value,
          etdDate: this.etdDate.value,
        }) || requiredDate()(val),
    )
    this.etbDate = new FieldStore<Dayjs | null>(
      initialValues?.etbDate ?? null,
      (val): string | undefined =>
        validateEtbDate({
          etaDate: this.etaDate.value,
          etbDate: val,
          etdDate: this.etdDate.value,
        }),
    )
    this.etbTime = new FieldStore<Dayjs | null>(
      initialValues?.etbTime ?? null,
      (val): string | undefined =>
        validateEtbDatetime({
          etaTime: this.etaTime.value,
          etbTime: val,
          etdTime: this.etdTime.value,
          etaDate: this.etaDate.value,
          etbDate: this.etbDate.value,
          etdDate: this.etdDate.value,
        }),
    )
    this.etdDate = new FieldStore<Dayjs | null>(
      initialValues?.etdDate ?? null,
      (val): string | undefined =>
        validateEtdDate({
          etaDate: this.etaDate.value,
          etbDate: this.etbDate.value,
          etdDate: val,
        }) || requiredDate()(val),
    )
    this.etdTime = new FieldStore<Dayjs | null>(
      initialValues?.etdTime ?? null,
      (val): string | undefined =>
        validateEtdDatetime({
          etaTime: this.etaTime.value,
          etbTime: this.etbTime.value,
          etdTime: val,
          etaDate: this.etaDate.value,
          etbDate: this.etbDate.value,
          etdDate: this.etdDate.value,
        }) || requiredDate()(val),
    )
    this.visitType = new FieldStore<VisitType | null>(initialValues?.visitType ?? 'Single visit')
    this.schedule = new VesselVisitScheduleFormStore(
      initialValues?.schedule ?? carrierVisitScheduleDefaultValues,
    )
    this.berths = new FieldStore<BerthResponseDto[]>(initialValues?.berths ?? [], requiredArray)
    this.length = new FieldStore<number | ''>(initialValues?.length ?? '')
    this.draftArrival = new FieldStore<number | null>(initialValues?.draftArrival ?? null)
    this.draftDeparture = new FieldStore<number | null>(initialValues?.draftDeparture ?? null)
    this.dockingSide = new FieldStore<BerthSide>(initialValues?.dockingSide ?? BerthSide.Port)
    this.pier = new FieldStore<PierResponseDto | null>(initialValues?.pier ?? null)
    this.qmmFrom = new FieldStore<number | ''>(initialValues?.qmmFrom ?? '')
    this.qmmTo = new FieldStore<number | ''>(initialValues?.qmmTo ?? '')
  }

  setData(data: Partial<VesselVisitBerthAndTimingFormStoreType>) {
    this.etaDate.setValue(data.etaDate ?? null)
    this.etaTime.setValue(data.etaTime ?? null)
    this.etbDate.setValue(data.etbDate ?? null)
    this.etbTime.setValue(data.etbTime ?? null)
    this.etdDate.setValue(data.etdDate ?? null)
    this.etdTime.setValue(data.etdTime ?? null)
    this.visitType.setValue(data.visitType ?? 'Single visit')
    this.schedule.setData(data.schedule ?? carrierVisitScheduleDefaultValues)
    this.length.setValue(data.length ?? '')
    this.draftArrival.setValue(data.draftArrival ?? null)
    this.draftDeparture.setValue(data.draftDeparture ?? null)
    this.dockingSide.setValue(data.dockingSide ?? BerthSide.Port)
    this.pier.setValue(data.pier ?? null)
    this.berths.setValue(data.berths ?? [])
    this.qmmFrom.setValue(data.qmmFrom ?? '')
    this.qmmTo.setValue(data.qmmTo ?? '')
  }

  // [Review] TODO: review & UT
  get conflictingBerthDraft() {
    const berths = this.berths.value ?? []
    const qmmFrom = this.qmmFrom.value ?? ''
    const qmmTo = this.qmmTo.value ?? ''
    const arrivalDraft = this.draftArrival.value ?? 0
    const departureDraft = this.draftDeparture.value ?? 0

    if (!berths.length || qmmFrom === '' || qmmFrom === 0 || qmmTo === '' || qmmTo === 0) {
      return null
    }

    const maxBerthDraft = this.calculateMaxBerthDraft(berths, qmmFrom, qmmTo)
    if (arrivalDraft > maxBerthDraft || departureDraft > maxBerthDraft) return maxBerthDraft

    return null
  }

  private calculateMaxBerthDraft(berths: BerthResponseDto[], qmmFrom: number, qmmTo: number) {
    const berthMaxDrafts = berths
      .map(b => b.maxDraft)
      .filter((d): d is number => typeof d === 'number' && !isNaN(d))

    const shallowestBerthDraft = Math.min(...berthMaxDrafts)

    let maxBerthDraft = shallowestBerthDraft

    // [REVIEW] TODO: Why do we need this?
    const overlappingBerths = berths.filter(
      berth =>
        (qmmFrom >= berth.quayMeterMarkFrom && qmmFrom < berth.quayMeterMarkTo) ||
        (qmmTo > berth.quayMeterMarkFrom && qmmTo <= berth.quayMeterMarkTo) ||
        (qmmFrom <= berth.quayMeterMarkFrom && qmmTo >= berth.quayMeterMarkTo),
    )

    const overlappingDrafts = overlappingBerths
      .map(b => b.maxDraft)
      .filter((d): d is number => typeof d === 'number' && !isNaN(d))

    const overlappingMaxDraft = overlappingDrafts.length ? Math.max(...overlappingDrafts) : null

    maxBerthDraft = overlappingMaxDraft ?? shallowestBerthDraft
    return maxBerthDraft
  }

  get berthsOfSelectedPier() {
    if (!this.pier.value) return []
    return this.berthItemStore.getBerthsByPier(this.pier.value.id)
  }

  get minQmmFrom() {
    if (!this.berthsOfSelectedPier.length) return 0
    return Math.min(...this.berthsOfSelectedPier.map(b => b.quayMeterMarkFrom))
  }

  get maxQmmTo() {
    if (!this.berthsOfSelectedPier.length) return 0
    return Math.max(...this.berthsOfSelectedPier.map(b => b.quayMeterMarkTo))
  }

  get isQmmOutOfRange() {
    if (
      !this.berthsOfSelectedPier.length ||
      !this.qmmFrom.value ||
      !this.qmmTo.value ||
      this.qmmFrom.value === 0 ||
      this.qmmTo.value === 0
    ) {
      return false
    }

    return this.qmmFrom.value < this.minQmmFrom || this.qmmTo.value > this.maxQmmTo
  }

  get data(): VesselVisitBerthAndTimingFormStoreType {
    return {
      etaDate: this.etaDate.value,
      etaTime: this.etaTime.value,
      etbDate: this.etbDate.value,
      etbTime: this.etbTime.value,
      etdDate: this.etdDate.value,
      etdTime: this.etdTime.value,

      visitType: this.visitType.value,
      schedule: this.schedule.data,

      length: this.length.value ? parseInt(this.length.value.toString()) : 0,
      draftArrival: this.draftArrival.value,
      draftDeparture: this.draftDeparture.value,

      dockingSide: this.dockingSide.value,
      pier: this.pier.value,

      qmmFrom: this.qmmFrom.value ? parseInt(this.qmmFrom.value.toString()) : 0,
      qmmTo: this.qmmTo.value ? parseInt(this.qmmTo.value.toString()) : 0,
    }
  }

  get isDirty(): boolean {
    return (
      this.etaDate.dirty ||
      this.etaTime.dirty ||
      this.etbDate.dirty ||
      this.etbTime.dirty ||
      this.etdDate.dirty ||
      this.etdTime.dirty ||
      this.visitType.dirty ||
      this.schedule.isDirty ||
      this.length.dirty ||
      this.draftArrival.dirty ||
      this.draftDeparture.dirty ||
      this.dockingSide.dirty ||
      this.pier.dirty ||
      this.qmmFrom.dirty ||
      this.qmmTo.dirty
    )
  }

  validate = () => {
    return validateSubForm(this)
  }

  reset() {
    this.setData(this.initialValues)
  }
}
